import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiClipboard, FiUsers, FiHeart, FiTarget, FiTrendingUp, FiMessageCircle, FiClock, FiArrowRight } = FiIcons;

const Assessments = () => {
  const assessments = [
    {
      id: 'leadership-style',
      icon: FiUsers,
      title: "Leadership Style Assessment",
      description: "Discover your natural leadership style and learn how to adapt it for different situations",
      duration: "2 minutes",
      questions: 5,
      color: "from-blue-500 to-blue-600",
      difficulty: "Beginner",
      insights: [
        "Identify your primary leadership style",
        "Understand your strengths and blind spots",
        "Learn when to adapt your approach"
      ]
    },
    {
      id: 'emotional-intelligence',
      icon: FiHeart,
      title: "Emotional Intelligence Evaluation",
      description: "Assess your emotional awareness, regulation, and social skills",
      duration: "2 minutes",
      questions: 5,
      color: "from-red-500 to-red-600",
      difficulty: "Intermediate",
      insights: [
        "Measure your emotional self-awareness",
        "Evaluate your empathy and social skills",
        "Get strategies for emotional regulation"
      ]
    },
    {
      id: 'communication-skills',
      icon: FiMessageCircle,
      title: "Communication Skills Analyzer",
      description: "Evaluate your communication effectiveness and listening abilities",
      duration: "1 minutes",
      questions: 5,
      color: "from-green-500 to-green-600",
      difficulty: "Beginner",
      insights: [
        "Assess your communication clarity",
        "Evaluate your active listening skills",
        "Identify areas for improvement"
      ]
    },
    {
      id: 'decision-making',
      icon: FiTarget,
      title: "Decision Making Assessment",
      description: "Analyze your decision-making process and problem-solving approach",
      duration: "2 minutes",
      questions: 5,
      color: "from-purple-500 to-purple-600",
      difficulty: "Advanced",
      insights: [
        "Understand your decision-making style",
        "Identify cognitive biases",
        "Learn structured decision frameworks"
      ]
    },
    {
      id: 'team-management',
      icon: FiTrendingUp,
      title: "Team Management Readiness",
      description: "Assess your readiness to manage and develop high-performing teams",
      duration: "2 minutes",
      questions: 5,
      color: "from-orange-500 to-orange-600",
      difficulty: "Intermediate",
      insights: [
        "Evaluate your team-building skills",
        "Assess your delegation abilities",
        "Understand your coaching potential"
      ]
    },
    {
      id: 'adaptive-leadership',
      icon: FiClipboard,
      title: "Adaptive Leadership Capacity",
      description: "Measure your ability to lead through change and uncertainty",
      duration: "1 minutes",
      questions: 5,
      color: "from-indigo-500 to-indigo-600",
      difficulty: "Advanced",
      insights: [
        "Assess your change management skills",
        "Evaluate your resilience",
        "Learn adaptive leadership strategies"
      ]
    }
  ];

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
            Leadership Assessments
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Gain deep insights into your leadership capabilities with our comprehensive assessments. Each evaluation provides personalized feedback and actionable recommendations.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {assessments.map((assessment, index) => (
            <motion.div
              key={assessment.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              <div className="p-8">
                <div className={`w-16 h-16 bg-gradient-to-r ${assessment.color} rounded-2xl flex items-center justify-center mb-6`}>
                  <SafeIcon icon={assessment.icon} className="text-white text-2xl" />
                </div>
                
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">{assessment.title}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(assessment.difficulty)}`}>
                    {assessment.difficulty}
                  </span>
                </div>
                
                <p className="text-gray-600 mb-6">{assessment.description}</p>
                
                <div className="flex items-center justify-between text-sm text-gray-500 mb-6">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiClock} className="text-lg" />
                    <span>{assessment.duration}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiClipboard} className="text-lg" />
                    <span>{assessment.questions} questions</span>
                  </div>
                </div>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">You'll discover:</h4>
                  <ul className="space-y-2">
                    {assessment.insights.map((insight, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2"></div>
                        <span className="text-sm text-gray-600">{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Link
                  to={`/assessments/${assessment.id}`}
                  className={`w-full bg-gradient-to-r ${assessment.color} text-white py-3 px-6 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2`}
                >
                  <span>Start Assessment</span>
                  <SafeIcon icon={FiArrowRight} className="text-lg" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Assessment Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mt-20 bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-12"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Take Our Assessments?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our scientifically-backed assessments provide actionable insights to accelerate your leadership development
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiTarget} className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Personalized Insights</h3>
              <p className="text-gray-600">Get tailored feedback based on your unique responses and leadership context</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiTrendingUp} className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Track Progress</h3>
              <p className="text-gray-600">Monitor your growth over time with detailed progress tracking and analytics</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiClipboard} className="text-white text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Actionable Plans</h3>
              <p className="text-gray-600">Receive specific development plans and resources to improve your skills</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Assessments;